import { useEffect, useRef } from 'react';

import { refreshAtLoadedDepth } from '@hooks/usePollAtLoadedDepth';
import {
  MY_TASKS_PROJECT_UPDATED_DEBOUNCE_MS,
  MY_TASKS_PROJECT_UPDATED_MAX_WAIT_MS,
} from '@src/constants/constants';
import { addProjectUpdatedListener } from '@utils/projectUpdatedEvent';

import type { ObservableQuery, OperationVariables } from '@apollo/client';

/**
 * - `'live'`: refresh on each projectUpdated event (debounced).
 * - `'paused'` (screen unfocused): note the event and refresh once on
 *   returning to `'live'`.
 * - `'background'` (app in the background): refresh once on returning to
 *   `'live'`, whether or not an event was noted. The socket can drop while
 *   the app is in the background, so an event can be missed without a trace.
 * - `undefined`: ignore the events.
 */
export type ProjectUpdatedRefreshMode = 'live' | 'paused' | 'background';

/**
 * TA-11139: refreshes `observableQuery` at its loaded depth when the app
 * receives a `projectUpdated` payload (see projectUpdatedEvent), replacing the
 * 30 s My Tasks poll. The backend publishes `projectUpdated` to every project
 * member when a task in the project is created, completed, updated, deleted
 * or assigned, so a task assigned to the viewer from another device reaches
 * My Tasks within about a second instead of up to 30 s.
 *
 * Uses the same fetch as usePollAtLoadedDepth, so the pages fetchMore loaded
 * stay loaded (TA-11016) and the query's own variables are left alone.
 *
 * A burst of events (one task edit can enqueue several jobs) is collapsed
 * into one request by a trailing debounce. The debounce has a maximum wait:
 * the event covers every project the viewer belongs to, so steady activity
 * elsewhere would otherwise keep postponing the refresh. An event that lands
 * while the query is already fetching retries after the debounce instead of
 * being dropped, since that fetch may have started before the change reached
 * the server.
 */
export const useRefreshOnProjectUpdated = <
  TData,
  TVariables extends OperationVariables,
>(
  observableQuery: ObservableQuery<TData, TVariables> | undefined,
  mode: ProjectUpdatedRefreshMode | undefined,
  skip = false
) => {
  // Both survive the effect re-running on pause/resume and on skip toggles,
  // so the refresh they ask for happens on the way back.
  const pendingRef = useRef(false);
  const wasInBackgroundRef = useRef(false);

  useEffect(() => {
    if (!observableQuery || !mode) return undefined;
    if (mode === 'background') wasInBackgroundRef.current = true;
    const live = mode === 'live' && !skip;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // When the first event of the current burst arrived; caps the debounce.
    let burstStartedAt: number | undefined;

    const refresh = () => {
      timer = undefined;
      if (refreshAtLoadedDepth(observableQuery)) {
        pendingRef.current = false;
        wasInBackgroundRef.current = false;
        burstStartedAt = undefined;
      } else {
        timer = setTimeout(refresh, MY_TASKS_PROJECT_UPDATED_DEBOUNCE_MS);
      }
    };
    const schedule = () => {
      const now = Date.now();
      burstStartedAt ??= now;
      const wait = Math.max(
        0,
        Math.min(
          MY_TASKS_PROJECT_UPDATED_DEBOUNCE_MS,
          burstStartedAt + MY_TASKS_PROJECT_UPDATED_MAX_WAIT_MS - now
        )
      );
      if (timer) clearTimeout(timer);
      timer = setTimeout(refresh, wait);
    };

    if (live && (pendingRef.current || wasInBackgroundRef.current)) schedule();

    const subscription = addProjectUpdatedListener(() => {
      pendingRef.current = true;
      if (live) schedule();
    });
    return () => {
      subscription.remove();
      if (timer) clearTimeout(timer);
    };
  }, [observableQuery, mode, skip]);
};
